import { cloneDeep } from 'lodash';
import { emptyValues, firebaseSystemUser } from '@/constants';
import { useStore } from '@/store';
import * as App from '@/types/app';
import * as Database from '@/types/database';
import { cloneGeoPoint, cloneTimestamp } from '@/utils';

const getModelOrEmpty = <
  TModel extends App.Model | Database.Model,
  TFunc extends (payload: string) => TModel | undefined,
  TReturnValue extends TModel
>(
  getById: TFunc,
  id: string,
  emptyValue: TModel
): TReturnValue => {
  return (getById(id) ?? emptyValue) as TReturnValue;
};

/**
 * Maps a database base model to an app base model. Maps from a userId to a user.
 * @param model The model to map.
 * @returns An app base model.
 */
export const mapDatabaseBaseModelToAppBaseModel = <
  TAppModel extends App.Model & Record<string, unknown>,
  TDatabaseModel extends Database.Model & Record<string, unknown>
>(
  model: TDatabaseModel,
  includeRestProps = true
): TAppModel => {
  const store = useStore();

  const { id, createdOn, lastUpdatedOn, ...restProps } = model;

  return {
    id: id ?? '',
    createdOn: createdOn && {
      timestamp: cloneDeep(createdOn.timestamp),
      user:
        createdOn.userId === firebaseSystemUser.id
          ? firebaseSystemUser.user
          : getModelOrEmpty(store.getters.users.getById, createdOn.userId ?? '', emptyValues.user)
    },
    lastUpdatedOn: lastUpdatedOn && {
      timestamp: cloneDeep(lastUpdatedOn.timestamp),
      user:
        lastUpdatedOn.userId === firebaseSystemUser.id
          ? firebaseSystemUser.user
          : getModelOrEmpty(store.getters.users.getById, lastUpdatedOn.userId ?? '', emptyValues.user)
    },
    ...(includeRestProps ? restProps : {})
  } as TAppModel;
};

/**
 * Maps an app base model to a database base model. Maps from a user to a userId.
 * @param model The model to map.
 * @returns A database base model.
 */
export const mapAppBaseModelToDatabaseBaseModel = <
  TAppModel extends App.Model & Record<string, unknown>,
  TDatabaseModel extends Database.Model & Record<string, unknown>
>(
  model: TAppModel,
  includeRestProps = true
): TDatabaseModel => {
  const { id, createdOn, lastUpdatedOn, ...restProps } = model;

  return {
    id: id ?? '',
    createdOn: createdOn && {
      timestamp: cloneDeep(createdOn.timestamp),
      userId: createdOn.user.id ?? ''
    },
    lastUpdatedOn: lastUpdatedOn && {
      timestamp: cloneDeep(lastUpdatedOn.timestamp),
      userId: lastUpdatedOn.user.id ?? ''
    },
    ...(includeRestProps ? restProps : {})
  } as TDatabaseModel;
};

export const mapAppVendorToDatabaseVendor = (vendor: App.Vendor): Database.Vendor => {
  return {
    ...mapAppBaseModelToDatabaseBaseModel(vendor),
    id: vendor.id ?? '',
    location: {
      address: vendor.location.address,
      geoPoint: cloneGeoPoint(vendor.location.geoPoint)
    },
    materials: vendor.materials.map(
      materialStock =>
        ({
          inStock: materialStock.inStock,
          materialId: materialStock.material.id,
          rate: cloneDeep(materialStock.rate)
        } as Database.VendorMaterialStock)
    ),
    name: vendor.name,
    phone: vendor.phone
  };
};

export const mapDatabaseVendorToAppVendor = (vendor: Database.Vendor): App.Vendor => {
  const store = useStore();

  return {
    ...mapDatabaseBaseModelToAppBaseModel(vendor),
    id: vendor.id,
    location: {
      address: vendor.location.address,
      geoPoint: cloneGeoPoint(vendor.location.geoPoint)
    },
    materials: vendor.materials.map(materialStock => ({
      inStock: materialStock.inStock,
      material: getModelOrEmpty(store.getters.materials.getById, materialStock.materialId, emptyValues.material),
      rate: {
        metric: materialStock.rate.metric,
        number: materialStock.rate.number
      }
    })),
    name: vendor.name,
    phone: vendor.phone
  };
};

export const mapAppBidToDatabaseBid = (bid: App.Bid): Database.Bid => ({
  ...mapAppBaseModelToDatabaseBaseModel(bid, false),
  clientId: bid.client.id,
  completionDate: cloneTimestamp(bid.completionDate),
  date: cloneTimestamp(bid.date),
  groupCompanyAddress: bid.groupCompanyAddress,
  groupCompanyName: bid.groupCompanyName,
  groupCompanyPhone: bid.groupCompanyPhone,
  id: bid.id ?? '',
  jobLocation: bid.jobLocation,
  jobName: bid.jobName,
  notes: bid.notes,
  materialList: bid.materialList.map(
    material =>
      ({
        materialDisplayName: material.materialDisplayName,
        vendorDisplayName: material.vendorDisplayName,
        truckDisplayName: material.truckDisplayName,
        materialId: material.material.id,
        vendorId: material.vendor.id,
        truckId: material.truck.id,
        taxValue: material.taxValue ?? 0,
        pricePerUnit: cloneDeep(material.pricePerUnit),
        quantity: material.quantity,
        location: material.location
      } as Database.MaterialListItem)
  )
});

export const mapDatabaseBidToAppBid = (bid: Database.Bid): App.Bid => {
  const store = useStore();

  return {
    ...mapDatabaseBaseModelToAppBaseModel(bid, false),
    client: getModelOrEmpty(store.getters.clients.getById, bid.clientId, emptyValues.client),
    completionDate: cloneTimestamp(bid.completionDate),
    date: cloneTimestamp(bid.date),
    groupCompanyAddress: bid.groupCompanyAddress,
    groupCompanyName: bid.groupCompanyName,
    groupCompanyPhone: bid.groupCompanyPhone,
    id: bid.id,
    jobLocation: bid.jobLocation,
    jobName: bid.jobName,
    notes: bid.notes,
    materialList: bid.materialList.map(material => ({
      materialDisplayName: material.materialDisplayName,
      vendorDisplayName: material.vendorDisplayName,
      truckDisplayName: material.truckDisplayName,
      material: getModelOrEmpty(store.getters.materials.getById, material.materialId, emptyValues.material),
      vendor: getModelOrEmpty(store.getters.vendors.getById, material.vendorId, emptyValues.vendor),
      truck: getModelOrEmpty(store.getters.trucks.getById, material.truckId, emptyValues.truck),
      taxValue: material.taxValue,
      pricePerUnit: cloneDeep(material.pricePerUnit),
      quantity: material.quantity,
      location: material.location
    }))
  };
};
